import { useCallback, useEffect, useRef, useState } from "react";
import { listEntries, type MonitoringEntry } from "@/src/services/monitoring_service";
import { ENTRY_FILTERS, type EntryFilter } from "./monitoring-filters";

export type MonitoringLoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; entries: MonitoringEntry[]; count: number };

const SEARCH_DEBOUNCE_MS = 500;

const FILTER_STORAGE_KEY = "monitoring-entries:filters";

type StoredFilters = { query: string; filter: EntryFilter; page: number };

function loadStoredFilters(): StoredFilters {
  const defaults: StoredFilters = { query: "", filter: "All Entries", page: 1 };
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<StoredFilters>;
    return {
      query: typeof parsed.query === "string" ? parsed.query : defaults.query,
      filter: ENTRY_FILTERS.includes(parsed.filter as EntryFilter)
        ? (parsed.filter as EntryFilter)
        : defaults.filter,
      page: typeof parsed.page === "number" && parsed.page > 0 ? parsed.page : defaults.page,
    };
  } catch {
    return defaults;
  }
}

function entriesFingerprint(entries: MonitoringEntry[]): string {
  return entries.map((e) => `${e.id}:${e.matches}:${e.newCount}:${e.status}`).join("|");
}

export function useMonitoringEntries({
  infoResolved,
  rowsPerPage,
}: {
  infoResolved: boolean;
  rowsPerPage: number;
}) {
  const [state, setState] = useState<MonitoringLoadState>({ status: "loading" });
  const [query, setQueryRaw] = useState(() => loadStoredFilters().query);
  const [debouncedQuery, setDebouncedQuery] = useState(() => loadStoredFilters().query);
  const [filter, setFilterRaw] = useState<EntryFilter>(() => loadStoredFilters().filter);
  const [page, setPage] = useState(() => loadStoredFilters().page);
  const [isFetchingSearch, setIsFetchingSearch] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const hasLoadedRef = useRef(false);
  const lastCountRef = useRef(0);
  const currentEntriesRef = useRef<MonitoringEntry[]>([]);
  const searchParamsRef = useRef({ query: debouncedQuery, filter, page, pageSize: rowsPerPage });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    searchParamsRef.current = { query: debouncedQuery, filter, page, pageSize: rowsPerPage };
  }, [debouncedQuery, filter, page, rowsPerPage]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const toStore: StoredFilters = { query: debouncedQuery, filter, page };
      window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(toStore));
    } catch {
      // ignore — sessionStorage unavailable (e.g. private browsing)
    }
  }, [debouncedQuery, filter, page]);

  /** Re-fetch the current page in the background (no loading state). */
  const refresh = useCallback(async () => {
    const params = searchParamsRef.current;
    const res = await listEntries({
      search: params.query.trim() || undefined,
      filter: params.filter,
      page: params.page,
      pageSize: params.pageSize,
    });
    // The user moved to another page/search meanwhile — that load wins.
    if (!res.ok || params !== searchParamsRef.current) return;
    const { entries, count } = res.value;
    const changed =
      count !== lastCountRef.current ||
      entriesFingerprint(entries) !== entriesFingerprint(currentEntriesRef.current);
    if (!changed) return;
    lastCountRef.current = count;
    currentEntriesRef.current = entries;
    setState({ status: "ready", entries, count });
  }, []);

  useEffect(() => {
    if (!infoResolved) return;
    let active = true;
    const cameFromEmptyState =
      lastCountRef.current === 0 && filter === "All Entries" && !debouncedQuery.trim();
    if (hasLoadedRef.current && !cameFromEmptyState) {
      setIsFetchingSearch(true);
    } else {
      setState({ status: "loading" });
    }
    listEntries({
      search: debouncedQuery.trim() || undefined,
      filter,
      page,
      pageSize: rowsPerPage,
    }).then((res) => {
      if (!active) return;
      setIsFetchingSearch(false);
      if (!res.ok) {
        setState({ status: "error" });
        return;
      }
      hasLoadedRef.current = true;
      const { entries, count } = res.value;
      lastCountRef.current = count;
      currentEntriesRef.current = entries;
      setState({ status: "ready", entries, count });
    });
    return () => {
      active = false;
    };
  }, [infoResolved, reloadKey, debouncedQuery, filter, page, rowsPerPage]);

  useEffect(() => {
    if (state.status !== "ready" || state.count === 0) return;
    const maxPage = Math.max(1, Math.ceil(state.count / rowsPerPage));
    if (page > maxPage) setPage(maxPage);
  }, [state, rowsPerPage, page]);

  const setQuery = useCallback((q: string) => {
    setPage(1);
    setQueryRaw(q);
  }, []);

  const setFilter = useCallback((f: EntryFilter) => {
    setPage(1);
    setFilterRaw(f);
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    state,
    isFetchingSearch,
    searching: query !== debouncedQuery || isFetchingSearch,
    query,
    setQuery,
    filter,
    setFilter,
    page,
    setPage,
    reload,
    reloadKey,
    refresh,
  };
}
